import { LineIcon } from '@/shared/ui/icons/LineIcon';
import { type LineIconProps } from '@/shared/ui/icons/base';

/**
 * UI-affordance line icons (Register A, docs/brand/iconography.md §2) — search/filter/sort/copy + the
 * language & calm-mode toggle glyphs + a neutral lock. Hand-authored single-stroke `currentColor` via
 * {@link LineIcon}; they inherit the surrounding text/tone colour (NOT AI colour art). One home for guardian
 * chrome line icons; add new ones here (or in `glyph-icons.tsx` for content/metric glyphs).
 */

/** Magnifier — the search affordance for `ListToolbar` / filter bars. */
export function SearchIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m16.5 16.5 4 4" />
    </LineIcon>
  );
}

/** Funnel — the filter affordance for list toolbars. */
export function FilterIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M3 5h18l-7 8.2V20l-4 1.5v-8.3L3 5Z" />
    </LineIcon>
  );
}

/** Stacked descending bars — the sort affordance. */
export function SortIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M4 6h16M6 12h12M9 18h6" />
    </LineIcon>
  );
}

/** Overlapping sheets — copy to clipboard. */
export function CopyIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </LineIcon>
  );
}

/** Globe — the language toggle. */
export function GlobeIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" />
    </LineIcon>
  );
}

/** Crescent moon — calm/dark mode on. */
export function MoonIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </LineIcon>
  );
}

/** Sun — calm/dark mode off (light). */
export function SunIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.4 1.4M17.6 17.6 19 19M19 5l-1.4 1.4M5 19l1.4-1.4" />
    </LineIcon>
  );
}

/** Padlock — a neutral read-only / capability-locked marker (kind, never punitive). */
export function LockIcon(props: LineIconProps): React.ReactElement {
  return (
    <LineIcon {...props}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </LineIcon>
  );
}
